import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { clientsApi } from '../api/api';
import { getDropdownOptions } from '../utils/dropdownConfig';
import './ClientDetail.css';

function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [workflowSteps, setWorkflowSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({});
  const [expandedStep, setExpandedStep] = useState(null);

  // Dropdown options state
  const [statusOptions, setStatusOptions] = useState([]);
  const [typeOptions, setTypeOptions] = useState([]);
  const [integratorOptions, setIntegratorOptions] = useState([]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [clientResponse, status, type, integrator] = await Promise.all([
          clientsApi.getOne(id),
          getDropdownOptions('status'),
          getDropdownOptions('type'),
          getDropdownOptions('integrator'),
        ]);
        const clientData = clientResponse.data;
        setClient(clientData);
        setWorkflowSteps(clientData.workflowSteps || []);
        // Format dates for display in Hungarian format
        setFormData({
          ...clientData,
          joinDate: clientData.joinDate ? formatDateForDisplay(clientData.joinDate) : '',
          leaveDate: clientData.leaveDate ? formatDateForDisplay(clientData.leaveDate) : '',
        });
        setStatusOptions(status);
        setTypeOptions(type);
        setIntegratorOptions(integrator);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [id]);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleDateChange = (e, fieldName) => {
    const value = e.target.value;
    // Allow only numbers, dots, and spaces
    const cleaned = value.replace(/[^\d.\s]/g, '');
    setFormData({
      ...formData,
      [fieldName]: cleaned,
    });
  };

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}. ${month}. ${day}.`;
  };

  const parseDateFromHungarian = (dateString) => {
    if (!dateString) return null;
    // Try to parse yyyy. MM. dd. format
    const match = dateString.match(/(\d{4})\.\s*(\d{1,2})\.\s*(\d{1,2})/);
    if (match) {
      const [, year, month, day] = match;
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day)).toISOString();
    }
    return null;
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      // Convert Hungarian date format to ISO before sending
      // Exclude MongoDB fields (_id, createdAt, updatedAt, __v) from the update
      const { _id, createdAt, updatedAt, __v, ...cleanFormData } = formData;
      const updateData = {
        ...cleanFormData,
        joinDate: parseDateFromHungarian(formData.joinDate) || formData.joinDate,
        leaveDate: parseDateFromHungarian(formData.leaveDate) || formData.leaveDate,
      };
      const response = await clientsApi.update(id, updateData);
      setClient(response.data);
      setFormData({
        ...response.data,
        joinDate: response.data.joinDate ? formatDateForDisplay(response.data.joinDate) : '',
        leaveDate: response.data.leaveDate ? formatDateForDisplay(response.data.leaveDate) : '',
      });
      setEditMode(false);
    } catch (error) {
      console.error('Error updating client:', error);
    }
  };

  const isStepRequired = (step) => {
    // Handle both boolean and string values for required field
    if (typeof step.required === 'boolean') return step.required;
    return step.required === 'Yes';
  };

  const canCompleteStep = (stepIndex) => {
    // Check all previous required steps are completed
    for (let i = 0; i < stepIndex; i++) {
      if (isStepRequired(workflowSteps[i]) && !workflowSteps[i].completed) {
        return false;
      }
    }
    return true;
  };

  const toggleWorkflowStep = async (stepName, stepIndex) => {
    try {
      const currentStep = client.workflowSteps.find((s) => s.stepName === stepName);
      const newCompleted = !currentStep?.completed;

      // If trying to mark as complete, check if previous required steps are done
      if (newCompleted && !canCompleteStep(stepIndex)) {
        alert('Please complete all previous required steps first.');
        return;
      }

      // If marking as incomplete, cascade to all following steps
      if (!newCompleted) {
        const updatedSteps = workflowSteps.map((step, idx) => {
          if (idx >= stepIndex) {
            return { ...step, completed: false, completedDate: null };
          }
          return step;
        });

        // Only send workflowSteps, not the full client object with MongoDB fields
        const response = await clientsApi.update(id, {
          workflowSteps: updatedSteps.map(step => ({
            stepName: step.stepName,
            description: step.description,
            responsiblePerson: step.responsiblePerson,
            required: step.required,
            completed: step.completed,
            completedDate: step.completedDate,
          })),
        });

        setClient(response.data);
        setFormData(response.data);
        setWorkflowSteps(response.data.workflowSteps || []);
      } else {
        // Just toggle the single step to complete
        const response = await clientsApi.updateWorkflowStep(id, stepName, newCompleted);
        setClient(response.data);
        setFormData(response.data);
        setWorkflowSteps(response.data.workflowSteps || []);
      }
    } catch (error) {
      console.error('Error updating workflow step:', error);
    }
  };

  const getStatusClass = (status) => {
    if (status === 'Aktív') return 'status-active';
    if (status === 'Nem aktív') return 'status-inactive';
    return 'status-progress';
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('hu-HU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <div className="card">Loading...</div>;
  }

  if (!client) {
    return <div className="card">Client not found</div>;
  }

  return (
    <div className="client-detail">
      <button className="btn btn-primary" onClick={() => navigate('/')}>
        Back to List
      </button>

      <div className="card">
        <div className="header-row">
          <h2>Client Details</h2>
          <button
            className="btn btn-primary"
            onClick={() => setEditMode(!editMode)}
          >
            {editMode ? 'Cancel Edit' : 'Edit'}
          </button>
        </div>

        {editMode ? (
          <form onSubmit={handleUpdate}>
            <div className="form-grid">
              <div className="form-group">
                <label>Partner Name</label>
                <input
                  type="text"
                  name="partnerName"
                  value={formData.partnerName}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label>Status</label>
                <select name="status" value={formData.status} onChange={handleInputChange}>
                  {statusOptions.map((option, index) => (
                    <option key={index} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Location</label>
                <input
                  type="text"
                  name="location"
                  value={formData.location || ''}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label>Contract Max Power (kW)</label>
                <input
                  type="text"
                  name="contractMaxPower"
                  value={formData.contractMaxPower || ''}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label>Type</label>
                <select name="type" value={formData.type || typeOptions[0]} onChange={handleInputChange}>
                  {typeOptions.map((option, index) => (
                    <option key={index} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Integrator</label>
                <select name="integrator" value={formData.integrator || ''} onChange={handleInputChange}>
                  <option value="">Select integrator...</option>
                  {integratorOptions.map((option, index) => (
                    <option key={index} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Interface</label>
                <input
                  type="text"
                  name="interface"
                  value={formData.interface || ''}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label>POD ID</label>
                <input
                  type="text"
                  name="podId"
                  value={formData.podId}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label>Partner Contact</label>
                <input
                  type="text"
                  name="partnerContact"
                  value={formData.partnerContact || ''}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label>Alteo Manager</label>
                <input
                  type="text"
                  name="alteoManager"
                  value={formData.alteoManager || ''}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label>Test REST API Key</label>
                <input
                  type="text"
                  name="testRestApiKey"
                  value={formData.testRestApiKey || ''}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label>Prod REST API Key</label>
                <input
                  type="text"
                  name="prodRestApiKey"
                  value={formData.prodRestApiKey || ''}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label>Join Date</label>
                <input
                  type="text"
                  name="joinDate"
                  value={formData.joinDate || ''}
                  onChange={(e) => handleDateChange(e, 'joinDate')}
                  placeholder="yyyy. MM. dd."
                />
              </div>

              <div className="form-group">
                <label>Leave Date</label>
                <input
                  type="text"
                  name="leaveDate"
                  value={formData.leaveDate || ''}
                  onChange={(e) => handleDateChange(e, 'leaveDate')}
                  placeholder="yyyy. MM. dd."
                />
              </div>

              <div className="form-group full-width">
                <label>Status Notes</label>
                <textarea
                  name="statusNotes"
                  value={formData.statusNotes || ''}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <button type="submit" className="btn btn-success">
              Save Changes
            </button>
          </form>
        ) : (
          <div className="details-grid">
            <div className="detail-item">
              <strong>Sequence Number:</strong>
              <span>#{client.sequenceNumber}</span>
            </div>
            <div className="detail-item">
              <strong>Partner Name:</strong>
              <span>{client.partnerName}</span>
            </div>
            <div className="detail-item">
              <strong>Status:</strong>
              <span className={`status-badge ${getStatusClass(client.status)}`}>{client.status}</span>
            </div>
            <div className="detail-item">
              <strong>Location:</strong>
              <span>{client.location || 'N/A'}</span>
            </div>
            <div className="detail-item">
              <strong>Contract Max Power:</strong>
              <span>{client.contractMaxPower || 'N/A'} kW</span>
            </div>
            <div className="detail-item">
              <strong>Type:</strong>
              <span>{client.type || 'N/A'}</span>
            </div>
            <div className="detail-item">
              <strong>Integrator:</strong>
              <span>{client.integrator || 'N/A'}</span>
            </div>
            <div className="detail-item">
              <strong>Interface:</strong>
              <span>{client.interface || 'N/A'}</span>
            </div>
            <div className="detail-item">
              <strong>POD ID:</strong>
              <span>{client.podId}</span>
            </div>
            <div className="detail-item">
              <strong>Partner Contact:</strong>
              <span>{client.partnerContact || 'N/A'}</span>
            </div>
            <div className="detail-item">
              <strong>Alteo Manager:</strong>
              <span>{client.alteoManager || 'N/A'}</span>
            </div>
            <div className="detail-item">
              <strong>Test REST API Key:</strong>
              <span>{client.testRestApiKey || 'N/A'}</span>
            </div>
            <div className="detail-item">
              <strong>Prod REST API Key:</strong>
              <span>{client.prodRestApiKey || 'N/A'}</span>
            </div>
            <div className="detail-item">
              <strong>Join Date:</strong>
              <span>{client.joinDate ? new Date(client.joinDate).toLocaleDateString('hu-HU') : 'N/A'}</span>
            </div>
            <div className="detail-item">
              <strong>Leave Date:</strong>
              <span>{client.leaveDate ? new Date(client.leaveDate).toLocaleDateString('hu-HU') : 'N/A'}</span>
            </div>
            <div className="detail-item">
              <strong>Created:</strong>
              <span>{formatDateTime(client.createdAt)}</span>
            </div>
            <div className="detail-item">
              <strong>Last Updated:</strong>
              <span>{formatDateTime(client.updatedAt)}</span>
            </div>
            {client.statusNotes && (
              <div className="detail-item full-width">
                <strong>Status Notes:</strong>
                <p className="notes">{client.statusNotes}</p>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="card">
        <h2>Integration Status</h2>
        <div className="workflow-linear-container">
          <div className="workflow-linear">
            {workflowSteps.map((step, index) => {
              const isCompleted = step.completed;
              const canComplete = canCompleteStep(index);
              const isExpanded = expandedStep === index;

              return (
                <React.Fragment key={index}>
                  <div
                    className={`workflow-step-node ${isCompleted ? 'completed' : ''} ${!isStepRequired(step) ? 'optional' : ''} ${!canComplete && !isCompleted ? 'disabled' : ''}`}
                    onClick={() => setExpandedStep(isExpanded ? null : index)}
                  >
                    <div className="step-circle">
                      {isCompleted ? '✓' : index + 1}
                    </div>
                    <div className="step-label">
                      {step.stepName}
                      {!isStepRequired(step) && <span className="optional-indicator">*</span>}
                    </div>
                  </div>
                  {index < workflowSteps.length - 1 && (
                    <div className={`step-connector ${isCompleted ? 'completed' : ''}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {expandedStep !== null && workflowSteps[expandedStep] && (
            <div className="workflow-details-panel">
              <div className="details-panel-header">
                <h3>{workflowSteps[expandedStep].stepName}</h3>
                {!isStepRequired(workflowSteps[expandedStep]) && (
                  <span className="optional-badge">Optional</span>
                )}
              </div>
              <p className="panel-description">{workflowSteps[expandedStep].description}</p>
              <p className="panel-responsible">
                <strong>Responsible:</strong> {workflowSteps[expandedStep].responsiblePerson}
              </p>
              {workflowSteps[expandedStep].completed && workflowSteps[expandedStep].completedDate && (
                <p className="panel-completed">
                  <strong>Completed:</strong> {new Date(workflowSteps[expandedStep].completedDate).toLocaleDateString()}
                </p>
              )}
              <div className="panel-actions">
                <button
                  className={`btn ${workflowSteps[expandedStep].completed ? 'btn-warning' : 'btn-success'}`}
                  onClick={() => toggleWorkflowStep(workflowSteps[expandedStep].stepName, expandedStep)}
                  disabled={!workflowSteps[expandedStep].completed && !canCompleteStep(expandedStep)}
                >
                  {workflowSteps[expandedStep].completed ? 'Mark as Incomplete' : 'Mark as Complete'}
                </button>
                {!canCompleteStep(expandedStep) && !workflowSteps[expandedStep].completed && (
                  <span className="disabled-hint">Complete previous required steps first</span>
                )}
              </div>
              {workflowSteps[expandedStep].completed && (
                <p className="panel-hint" style={{ marginTop: '1rem', fontSize: '0.875rem', color: '#666', fontStyle: 'italic' }}>
                  Note: Marking this step as incomplete will also reset all following steps.
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ClientDetail;
